import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { SyncNowState } from './hooks/useSync';
import { WARN_TEXT, RADIUS, TONE } from './lib/brand';
import { ink } from './lib/tone';

const STEPS = ['Sync requested', 'Grok Bot acked', 'New data received'];

/**
 * "Sync now" with its three-step tracker. The button counts down the 30 s rate
 * limit; after 3 minutes without new data the last step reads "No newer snapshot
 * yet" and Retry appears.
 */
export function SyncNow({ state, onSync }: { state: SyncNowState & { sync?: unknown }; onSync: () => void }) {
  const { phase, steps, cooldown, error } = state;
  const running = phase === 'running';
  const label = cooldown > 0 ? `Sync now (${cooldown}s)` : phase === 'noNewer' ? 'Retry sync' : 'Sync now';
  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 0.6 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={onSync}
        disabled={cooldown > 0 || (running && !steps[0])}
        data-testid="sync-now"
        sx={{ textTransform: 'none', borderRadius: RADIUS.pill }}
      >
        {label}
      </Button>
      {phase !== 'idle' && phase !== 'error' && (
        <Box component="ol" data-testid="sync-tracker" aria-label="Sync progress" sx={{ display: 'flex', gap: 1.2, m: 0, p: 0, listStyle: 'none', flexWrap: 'wrap' }}>
          {STEPS.map((t, i) => {
            const done = steps[i];
            const failed = phase === 'noNewer' && i === 2;
            return (
              <Typography key={t} component="li" variant="caption" data-done={done} sx={{ color: done ? ink(TONE.good.fg, TONE.good.border) : failed ? WARN_TEXT : 'text.secondary', fontWeight: done ? 700 : 400 }}>
                {done ? '✓ ' : failed ? '! ' : running && (i === 0 || steps[i - 1]) ? '… ' : '○ '}
                {failed ? 'No newer snapshot yet' : t}
              </Typography>
            );
          })}
        </Box>
      )}
      {phase === 'error' && error && <Typography variant="caption" role="alert" sx={{ color: 'error.main' }}>{error}</Typography>}
    </Box>
  );
}
